import React, { useState, useEffect, useRef } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import '../styles/Policy.css';
import SEOHead from '../components/SEOHead';
import ScrollProgressIndicator from '../components/ScrollProgressIndicator';
import CallToAction from '../components/CallToAction';

const policies = [
  {
    title: "Website Design & Development",
    icon: "🌐",
    content: [
      "All design and development projects require a 50% upfront payment before work begins, with the remaining 50% due upon project completion and client approval.",
      "We provide up to 3 rounds of minor revisions during the design phase. Major changes or additional features requested after final approval will be billed at our standard hourly rate of ₹2,500/hour.",
      "Project timelines are estimated based on scope and complexity. Delays due to client feedback or content provision may affect delivery dates.",
      "We provide 30 days of free technical support post-launch, including bug fixes and minor adjustments. Ongoing maintenance packages are available separately.",
      "Source code and design files are delivered upon full payment completion. We retain the right to showcase completed projects in our portfolio unless specifically requested otherwise.",
      "Domain and hosting setup assistance is included. Clients are responsible for annual domain and hosting renewal fees.",
      "Mobile responsiveness and cross-browser compatibility (Chrome, Firefox, Safari, Edge) are included in all web development projects."
    ]
  },
  {
    title: "SEO Optimization Services",
    icon: "📈",
    content: [
      "SEO services require a minimum 3-month commitment to achieve measurable results. Search engine optimization is a gradual process that requires consistent effort and time.",
      "We follow only white-hat, ethical SEO practices approved by Google. We do not guarantee specific search engine rankings, as search algorithms are constantly evolving.",
      "Monthly SEO packages include keyword research, on-page optimization, technical SEO audits, content optimization, and detailed performance reports.",
      "Local SEO services focus on improving visibility for location-based searches and include Google My Business optimization and local directory submissions.",
      "We provide monthly progress reports showing keyword rankings, organic traffic growth, and technical improvements implemented.",
      "Clients must not engage other SEO services during our contract period, as conflicting strategies can negatively impact results.",
      "Content creation for SEO purposes (blogs, landing pages) may require additional investment and will be quoted separately based on requirements."
    ]
  },
  {
    title: "Digital Marketing & Social Media Management",
    icon: "📱",
    content: [
      "Monthly social media packages include a predetermined number of posts (8-20 posts depending on package), story content, and community management during business hours (9 AM - 6 PM IST).",
      "Custom campaigns, paid advertising, and influencer collaborations require separate budgets and will be quoted based on scope and platform requirements.",
      "We require 7 days advance notice for service cancellation. No refunds are provided for partial months, but services will continue until the paid period expires.",
      "Content calendars are shared for approval at the beginning of each month. Last-minute changes may incur additional charges.",
      "We maintain professional standards and brand consistency across all platforms. Content that violates platform guidelines or brand values will not be posted.",
      "Performance metrics and insights are provided monthly, including engagement rates, reach, impressions, and audience growth.",
      "Crisis management and reputation monitoring are available as add-on services and are highly recommended for businesses in sensitive industries."
    ]
  },
  {
    title: "Content Creation & Copywriting",
    icon: "✍️",
    content: [
      "We retain creative rights and ownership of all content until full payment is received. Upon payment, full rights transfer to the client.",
      "Each content piece includes up to 2 rounds of revisions. Additional revisions beyond this limit will be charged at ₹500 per revision round.",
      "Clients must provide all necessary brand materials, guidelines, and reference content before production begins to ensure alignment with brand voice.",
      "Delivery timelines vary based on content type: blog posts (3-5 days), website copy (5-7 days), marketing materials (7-10 days).",
      "All content is original and goes through plagiarism checks. We provide Copyscape reports upon request for written content.",
      "Rush orders (less than 48-hour turnaround) incur a 50% urgency fee and are subject to availability.",
      "Content strategy consultation and editorial calendars are available as separate services to maximize content effectiveness."
    ]
  },
  {
    title: "Video Production & Editing",
    icon: "🎬",
    content: [
      "Video production services include pre-production planning, filming (if applicable), editing, color correction, and final delivery in requested formats.",
      "Clients are responsible for script approval, location arrangements, and timely feedback during the revision process to avoid project delays.",
      "We provide up to 3 rounds of editing revisions. Major changes to concept or significant re-editing will be treated as a new project.",
      "Raw footage and project files are retained for 30 days post-delivery for potential future edits. Extended retention requires additional storage fees.",
      "Video content delivery includes multiple formats optimized for different platforms (YouTube, Instagram, Facebook, website embedding).",
      "Music and stock footage licensing fees are included in standard packages. Premium music or footage may require additional licensing fees.",
      "Live streaming setup and technical support are available as specialized services with separate pricing structures."
    ]
  },
  {
    title: "Facebook & Google Ads Management",
    icon: "🎯",
    content: [
      "We manage advertising campaigns through your verified business ad accounts only. We never use agency accounts for client campaigns to ensure full transparency and ownership.",
      "Ad budgets must be prepaid and are managed separately from our service fees. Minimum monthly ad spend is ₹10,000 for effective campaign optimization.",
      "We do not guarantee specific lead quantities, conversion rates, or ROAS (Return on Ad Spend), as performance depends on various market factors and business variables.",
      "Campaign setup includes audience research, ad creative development, landing page optimization recommendations, and conversion tracking implementation.",
      "Weekly performance reports include key metrics such as impressions, clicks, conversions, cost per lead, and optimization recommendations.",
      "Ad account access and admin privileges are required for effective campaign management. We follow strict security protocols to protect account information.",
      "A/B testing is conducted regularly to optimize ad performance, and we recommend running campaigns for at least 30 days for optimal data collection and optimization."
    ]
  },
  {
    title: "E-commerce Development",
    icon: "🛒",
    content: [
      "E-commerce projects include shopping cart functionality, payment gateway integration, inventory management, and order processing systems.",
      "Payment gateway setup requires merchant account verification and may involve additional fees from payment providers (not included in our service fee).",
      "Product catalog setup for up to 100 products is included. Additional products beyond this limit will be charged at ₹50 per product for data entry and optimization.",
      "We provide training sessions for store management, order processing, and basic maintenance. Advanced training is available as a separate service.",
      "Security features include SSL certificates, secure payment processing, and basic fraud protection. Advanced security measures may require additional investment.",
      "Mobile optimization and responsive design are standard features, ensuring optimal shopping experience across all devices.",
      "Ongoing maintenance packages include security updates, backup management, and technical support to keep your store running smoothly."
    ]
  },
  {
    title: "General Terms & Conditions",
    icon: "📋",
    content: [
      "All project quotes are valid for 30 days from the date of proposal. Market changes or scope modifications may affect pricing after this period.",
      "Intellectual property rights transfer to clients upon full payment completion. We reserve the right to use completed work in our portfolio and marketing materials.",
      "Force majeure events (natural disasters, government restrictions, internet outages) may affect project timelines and will not be considered breach of contract.",
      "Dispute resolution will be attempted through direct communication first. If unresolved, disputes will be handled through arbitration in Bhopal, Madhya Pradesh.",
      "Service level agreements (SLAs) are defined per project type. Emergency support outside business hours is available at premium rates.",
      "Data confidentiality and client information security are paramount. We maintain strict privacy policies and never share client data with third parties.",
      "Regular backups and version control are maintained for all active projects. However, clients are responsible for maintaining their own data backups post-project completion."
    ]
  }
];

const PolicyPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activePolicy, setActivePolicy] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Video ref for handling video loading and optimization
  const backgroundVideoRef = useRef(null);

  // Scroll to top on mount and handle scroll events
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Enhanced video loading and error handling
  useEffect(() => {
    const handleVideoLoad = (videoElement) => {
      if (videoElement) {
        videoElement.addEventListener('loadeddata', () => {
          setVideoLoaded(true);
        });

        videoElement.addEventListener('error', (e) => {
          console.warn(`Video loading issue:`, e);
          // Fallback: Hide video and show gradient background
          videoElement.style.display = 'none';
        });

        // Optimize video playback
        videoElement.addEventListener('canplay', () => {
          videoElement.play().catch(e => {
            console.warn(`Video autoplay failed:`, e);
          });
        });
      }
    };

    if (backgroundVideoRef.current) {
      handleVideoLoad(backgroundVideoRef.current);
    }
  }, []);

  // Spring animation for scroll-based effects
  const springConfig = { stiffness: 120, damping: 25, restDelta: 0.001 };
  const springScrollY = useSpring(scrollY, springConfig);

  const headerOpacity = useTransform(
    springScrollY,
    [0, 400],
    [1, 0.85],
    { clamp: true }
  );

  const headerScale = useTransform(
    springScrollY,
    [0, 400],
    [1, 0.96],
    { clamp: true }
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  // Toggle policy accordion
  const togglePolicy = (index) => {
    setActivePolicy(activePolicy === index ? null : index);
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const cards = document.querySelectorAll('.policy-card');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="policy-page">
      <SEOHead 
        page="policy"
        title="Service Policies & Terms | QuantumFrame Studio"
        description="Comprehensive service policies and terms of service for web development, SEO, digital marketing, and other services offered by QuantumFrame Studio in Bhopal."
        keywords="service policy, terms of service, web development policy, SEO terms, digital marketing policy, QuantumFrame Studio Bhopal"
      />
      <ScrollProgressIndicator type="line" />

      {/* Single Background Video - Fixed for entire page */}
      <video 
        ref={backgroundVideoRef}
        autoPlay 
        muted 
        loop 
        className="policy-background-video"
        aria-hidden="true"
        title="QuantumFrame Studio professional services background video"
      >
        <source src="/assets/video/full-backgroundvid.mp4" type="video/mp4" />
        <source src="/assets/video/full-backgroundvid.webm" type="video/webm" />
        <source src="/assets/video/full-backgroundvid.ogv" type="video/ogg" />
        Your browser does not support the video tag.
      </video>

      {/* Hero Section */}
      <motion.section
        className="policy-hero-section"
        initial="hidden"
        animate="visible"
        variants={heroVariants}
        style={{ opacity: headerOpacity, scale: headerScale }}
        role="banner"
        aria-label="Policy page hero section"
      >
        <motion.div
          className="policy-hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="policy-hero-text"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
            <motion.span
              className="policy-hero-tagline"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              whileHover={{ scale: 1.05 }}
            >
              Transparent Business Practices & Service Excellence
            </motion.span>

            <motion.h1
              className="policy-hero-title"
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
            >
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                Service Policies
              </motion.span>
              <br />
              <motion.span 
                className="policy-title-highlight"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
              >
                & Terms of Service
              </motion.span>
            </motion.h1>
            
            <motion.p
              className="policy-hero-description"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.8 }}
            >
              Clear, comprehensive policies for all our professional services. 
              We believe in transparency and want you to understand exactly what 
              to expect when working with QuantumFrame Studio.
            </motion.p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Main Content Container */}
      <motion.div
        className="policy-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.div className="policies-grid">
          {policies.map((policy, index) => (
            <motion.div
              className={`policy-card ${activePolicy === index ? 'active' : ''}`}
              key={index}
              variants={cardVariants}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.4), 0 10px 25px rgba(255, 107, 107, 0.15)"
              }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              layout
            >
              <motion.div 
                className="policy-card-header"
                onClick={() => togglePolicy(index)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                role="button"
                tabIndex={0}
                aria-expanded={activePolicy === index}
                aria-controls={`policy-content-${index}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    togglePolicy(index);
                  }
                }}
              >
                <motion.div 
                  className="policy-icon"
                  whileHover={{ 
                    scale: 1.1, 
                    rotateY: 10,
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)"
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  {policy.icon}
                </motion.div>
                <div className="policy-header-content">
                  <motion.h2
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  >
                    {policy.title}
                  </motion.h2>
                  <motion.div 
                    className="expand-indicator"
                    animate={{ 
                      rotate: activePolicy === index ? 180 : 0,
                      scale: activePolicy === index ? 1.1 : 1
                    }}
                    transition={{ 
                      duration: 0.3,
                      type: "spring",
                      stiffness: 200,
                      damping: 20
                    }}
                    whileHover={{ scale: 1.2 }}
                  >
                    ⌄
                  </motion.div>
                </div>
              </motion.div>

              <motion.div
                className="policy-content"
                variants={contentVariants}
                initial="hidden"
                animate={activePolicy === index ? "visible" : "hidden"}
                id={`policy-content-${index}`}
                role="region"
                aria-labelledby={`policy-header-${index}`}
              >
                <div className="policy-content-inner">
                  {policy.content.map((point, pointIndex) => (
                    <motion.div
                      key={pointIndex}
                      className="policy-point"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ 
                        opacity: activePolicy === index ? 1 : 0, 
                        x: activePolicy === index ? 0 : -30 
                      }}
                      transition={{ 
                        delay: activePolicy === index ? pointIndex * 0.1 : 0,
                        duration: 0.5,
                        ease: [0.4, 0, 0.2, 1]
                      }}
                      whileHover={{ x: 5 }}
                    >
                      <motion.span 
                        className="point-number"
                        whileHover={{ 
                          scale: 1.1,
                          boxShadow: "0 8px 25px rgba(255, 107, 107, 0.4)"
                        }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        {pointIndex + 1}
                      </motion.span>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: activePolicy === index ? 1 : 0 }}
                        transition={{ 
                          delay: activePolicy === index ? pointIndex * 0.1 + 0.2 : 0,
                          duration: 0.4
                        }}
                      >
                        {point}
                      </motion.p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Call to Action Component */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <CallToAction />
      </motion.div>
    </div>
  );
};

export default PolicyPage;